import styled from 'styled-components';

const Wrapper = styled.div`
  /* Hero Section */
  .hero-section {
    background: linear-gradient(135deg, #F0EBE5 0%, #E8E3DD 100%);
    padding: 4rem 1.5rem;
    text-align: center;

    @media (min-width: 768px) {
      padding: 6rem 1.5rem;
    }
  }

  .hero-content {
    max-width: 1280px;
    margin: 0 auto;
  }

  .breadcrumb {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 1.5rem;

    a {
      color: #6b7280;
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: #8D7B68;
      }
    }

    .current {
      color: #111718;
      font-weight: 500;
    }
  }

  .hero-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    font-weight: 500;
    color: #111718;
    line-height: 1.2;
    margin: 0;

    @media (min-width: 768px) {
      font-size: 3.75rem;
    }

    br {
      display: none;

      @media (min-width: 768px) {
        display: block;
      }
    }
  }

  /* Story Section */
  .story-section {
    padding: 5rem 1.5rem;

    @media (min-width: 768px) {
      padding: 8rem 1.5rem;
    }
  }

  .story-container {
    max-width: 1280px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3rem;

    @media (min-width: 768px) {
      flex-direction: row;
      gap: 3rem;
    }

    @media (min-width: 1024px) {
      gap: 6rem;
    }
  }

  .story-image-wrapper {
    width: 100%;
    position: relative;

    @media (min-width: 768px) {
      width: 50%;
    }
  }

  .story-image {
    aspect-ratio: 4/5;
    overflow: hidden;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    transition: transform 0.7s ease-out;

    @media (min-width: 768px) {
      aspect-ratio: 1/1;
    }

    &:hover {
      transform: scale(1.02);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .image-border {
    display: none;

    @media (min-width: 768px) {
      display: block;
      position: absolute;
      bottom: -1.5rem;
      left: -1.5rem;
      width: 100%;
      height: 100%;
      border: 2px solid rgba(141, 123, 104, 0.2);
      border-radius: 12px;
      z-index: -1;
    }
  }

  .story-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (min-width: 768px) {
      width: 50%;
    }
  }

  .accent-line {
    width: 4rem;
    height: 4px;
    background: #8D7B68;
    margin-bottom: 2rem;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: #111718;
    margin: 0 0 2rem;

    @media (min-width: 768px) {
      font-size: 2.5rem;
    }
  }

  .story-text {
    font-size: 1.125rem;
    line-height: 1.75;
    color: #6b7280;
    font-weight: 300;

    p {
      margin: 0 0 1.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .stats {
    display: flex;
    gap: 4rem;
    margin-top: 2.5rem;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
  }

  .stat-number {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: #8D7B68;
    font-weight: 700;
    line-height: 1;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 0.5rem;
  }

  /* Values Section */
  .values-section {
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 5rem 1.5rem;
  }

  .values-container {
    max-width: 1280px;
    margin: 0 auto;
  }

  .values-header {
    text-align: center;
    margin-bottom: 4rem;
  }

  .values-subtitle {
    color: #8D7B68;
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
  }

  .values-title {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    margin-top: 0.75rem;
    color: #111718;

    @media (min-width: 768px) {
      font-size: 2.5rem;
    }
  }

  .values-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .value-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.5rem;
    border-radius: 12px;
    transition: all 0.3s;

    &:hover {
      background: #fafaf9;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
  }

  .value-icon {
    width: 4rem;
    height: 4rem;
    border-radius: 50%;
    background: linear-gradient(135deg, #F0EBE5 0%, #E8E3DD 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8D7B68;
    font-size: 1.875rem;
    margin-bottom: 1.5rem;
  }

  .value-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0.75rem;
    color: #111718;
  }

  .value-text {
    color: #6b7280;
    line-height: 1.75;
    font-size: 0.875rem;
  }

  /* CTA Section */
  .cta-section {
    background: #8D7B68;
    padding: 6rem 1.5rem;
    position: relative;
    overflow: hidden;
  }

  .cta-pattern {
    position: absolute;
    inset: 0;
    opacity: 0.1;
    pointer-events: none;
    background-image: 
      linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  .cta-content {
    max-width: 56rem;
    margin: 0 auto;
    text-align: center;
    position: relative;
    z-index: 10;
  }

  .cta-title {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: #ffffff;
    margin-bottom: 1.5rem;
    line-height: 1.3;

    @media (min-width: 768px) {
      font-size: 3rem;
    }
  }

  .cta-text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.125rem;
    margin-bottom: 2.5rem;
    font-weight: 300;
    max-width: 48rem;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.75;
  }

  .cta-button {
    display: inline-block;
    background: #ffffff;
    color: #8D7B68;
    padding: 1rem 2.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.875rem;
    border-radius: 4px;
    text-decoration: none;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    transition: all 0.3s;

    &:hover {
      background: #f3f4f6;
      transform: translateY(-2px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
    }
  }
`;

export default Wrapper;
